import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { systemApi } from '@/api/system';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Browse } from '@/components/chat/Browse';
import { Playground } from '@/components/chat/Playground';
import { DiagnosePanel } from '@/components/chat/DiagnosePanel';
import { SupportPanel } from '@/components/chat/SupportPanel';

const TAB_KEY = 'zorro-ai-chat-tab';

export function Chat(): JSX.Element {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [testVersion] = React.useState<number | null>(() => Number(searchParams.get('promptVersion')) || null);
  const [tab, setTab] = React.useState<string>(() =>
    testVersion ? 'chat' : (sessionStorage.getItem(TAB_KEY) ?? 'chat'),
  );
  React.useEffect(() => {
    if (searchParams.has('promptVersion')) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);
  const [resumeSessionId, setResumeSessionId] = React.useState<string | null>(null);
  React.useEffect(() => {
    sessionStorage.setItem(TAB_KEY, tab);
  }, [tab]);

  const handleResumeSession = (sessionId: string): void => {
    setResumeSessionId(sessionId);
    setTab('chat');
  };

  const system = useQuery({ queryKey: ['system'], queryFn: systemApi.get, staleTime: 30_000 });
  const envDisabled = false;
  const runtimeDisabled = !system.data?.bot?.enabled;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('chat.pageTitle')}</h1>
        <p className="text-sm text-muted-foreground">
          <Trans i18nKey="chat.pageSubtitle" components={{ b: <strong /> }} />
        </p>
      </header>

      {envDisabled ? (
        <Alert variant="destructive">
          <Lock className="h-4 w-4" />
          <AlertTitle>{t('chat.envDisabledTitle')}</AlertTitle>
          <AlertDescription>
            <Trans i18nKey="chat.envDisabledBody" components={{ code: <code />, em: <em /> }} />
          </AlertDescription>
        </Alert>
      ) : runtimeDisabled ? (
        <Alert variant="warning">
          <Lock className="h-4 w-4" />
          <AlertTitle>{t('chat.runtimeDisabledTitle')}</AlertTitle>
          <AlertDescription>
            <Trans
              i18nKey="chat.runtimeDisabledBody"
              components={{ link: <a href="/system" className="underline" /> }}
            />
          </AlertDescription>
        </Alert>
      ) : null}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="chat">{t('chat.tabs.chat')}</TabsTrigger>
          <TabsTrigger value="diagnose">{t('chat.tabs.diagnose')}</TabsTrigger>
          <TabsTrigger value="support">{t('chat.tabs.support')}</TabsTrigger>
          <TabsTrigger value="browse">{t('chat.tabs.browse')}</TabsTrigger>
        </TabsList>
        <TabsContent value="chat">
          <Playground
            resumeSessionId={resumeSessionId}
            onResumeConsumed={() => setResumeSessionId(null)}
            initialPromptVersion={testVersion}
          />
        </TabsContent>
        <TabsContent value="diagnose">
          <DiagnosePanel initialPromptVersion={testVersion} />
        </TabsContent>
        <TabsContent value="support">
          <SupportPanel initialPromptVersion={testVersion} />
        </TabsContent>
        <TabsContent value="browse">
          <Browse onResume={handleResumeSession} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
